/**
 * 편집 모드(`?editReportId=`)의 **점검 컨텍스트** — 저장된 문서가 어느 점검에 묶여 있는가.
 *
 * 신규 작성은 `?inspectionIds=`(진입 query)가 점검 컨텍스트지만, 편집은 query에 점검이 없다 —
 * 문서 자신이 junction(`reportInspections`, sortOrder = 고른 순서)으로 점검을 쥐고 있다. 편집
 * 모드에서 평가 방식을 바꿔 위험도를 재resolve하려면(결정 2026-09-14 「편집 모드 평가 방식 변경」)
 * 서버 prefill에 이 점검들을 실어야 `finding-risk-assessments` 조립이 새 방식으로 행을 돌려준다
 * — 안 실으면 응답에 행 배열이 없어 병합이 행을 건드리지 못하고 옛 어휘의 위험도가 그대로 남는다.
 *
 * 순서는 junction `sortOrder` 오름차순(서버 report 경로 조립 순서와 같은 축 — 다중 점검 합본의
 * 통합 연번이 이 순서를 따른다). junction이 비면 레거시 단일 연결(`report.inspection`)로 폴백,
 * 그것도 없으면 undefined(점검 컨텍스트 없음 — 위험도 축이 없는 문서).
 *
 * 페이지에서 떼어낸 이유: 페이지 spec은 rc를 통째로 mock해 이 파생을 간접으로만 잰다 — 순서·폴백은
 * 여기 spec이 직접 잰다.
 */
import type { ReportComposerPreviewContext } from '@safetyhub/shared';
import type { MergeMethodReresolveOptions } from '@safetyhub/report-composer';
import type { Report } from '../../lib/reports';

export function resolveEditReportInspectionIds(
  report: Pick<Report, 'reportInspections' | 'inspection'> | null | undefined,
): string[] | undefined {
  if (!report) return undefined;
  const junction = report.reportInspections ?? [];
  if (junction.length > 0) {
    return [...junction]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((ri) => ri.inspectionId)
      .filter((id): id is string => typeof id === 'string' && id !== '');
  }
  const legacyId = report.inspection?.id;
  return legacyId ? [legacyId] : undefined;
}

/**
 * 편집 모드의 서버 prefill **컨텍스트** — 재료는 문서다(현장 = `report.siteId`, 공종 = `splitGroupLabel`,
 * 조직 = 호출자 companyId). 편집 로드 prefill(콤보 후보 rows)과 평가 방식 변경 재resolve 두 자리가 같은
 * 재료를 쓰므로 한 벌로 둔다 — 호출부가 같은 객체를 손으로 조립하지 말 것.
 *
 * 값이 없는 키는 싣지 않는다(종전 `?? undefined`와 같은 결과 — 서버가 키 부재/undefined를 같게 본다).
 * 점검(`inspectionIds`)은 재resolve만 싣는다 — 로드 prefill은 콤보 후보만 필요해 점검을 싣지 않지만,
 * 재resolve는 위험도 행을 새 방식으로 다시 받아야 하므로 점검이 있어야 한다(없으면 응답에 행이 없어
 * 병합이 행을 못 건드린다). 비어 있으면(0개) 키를 싣지 않는다.
 */
export function editReportPrefillContext(
  report: Pick<Report, 'siteId' | 'splitGroupLabel'>,
  companyId: string | undefined,
  extra: { inspectionIds?: string[] } = {},
): ReportComposerPreviewContext {
  const ctx: ReportComposerPreviewContext = {
    companyId: companyId || undefined,
    siteId: report.siteId ?? undefined,
    workGroupLabel: report.splitGroupLabel ?? undefined,
  };
  if (extra.inspectionIds && extra.inspectionIds.length > 0) {
    ctx.inspectionIds = extra.inspectionIds;
  }
  return ctx;
}

/** 평가 방식 변경 재resolve 요청 — 컨텍스트·템플릿 키·병합 기준(편집/신규가 갈리는 세 축). */
export interface MethodReresolveRequest {
  context: ReportComposerPreviewContext;
  templateKey: string;
  mergeOptions: MergeMethodReresolveOptions | undefined;
}

/**
 * 평가 방식 변경 재resolve의 **요청 형상** — 순수 판정(new-composed의 재resolve effect가 부른다).
 * 편집이 신규와 다른 세 축을 한 곳에 모은다(결정 2026-09-14 RA 전환 문서 「편집 모드 평가 방식 변경」):
 *  ① 컨텍스트: 신규는 진입 query(`suggestContext`), 편집은 문서(`editReportPrefillContext` + junction 점검).
 *  ② 템플릿 키: 신규는 라우트 키, 편집은 문서의 templateKey(스냅샷 세대의 양식 — 로드 prefill과 같은 키,
 *     없으면 템플릿 자신의 key).
 *  ③ 병합: 편집은 `rowBasis:'current'`(저장 content가 행의 정본 — 지운 행이 되살아나거나 추가 행이
 *     사라지면 안 된다), 신규는 기본(fresh).
 *
 * `null`이면 재조회하지 않는다:
 *  - 편집 모드인데 문서를 아직 쥐고 있지 않거나 **다른 문서**다(형제 칩 이동 직후 경계 — 앞 문서로
 *    재조회하지 않게 id를 대조한다).
 *  - prefill과 동일한 컨텍스트 게이트: 조직·현장·점검이 전부 없으면 재조회 불가.
 */
export function resolveMethodReresolveRequest(args: {
  isEditMode: boolean;
  editReportId: string | null | undefined;
  /** 편집 로드가 쥔 문서(ref 값) — id가 `editReportId`와 다르면 앞 문서로 본다. */
  loadedEditReport: Report | null | undefined;
  companyId: string;
  siteIdParam: string | null | undefined;
  /** 신규 작성의 진입 query 점검. */
  inspectionIds: string[] | undefined;
  /** 신규 작성의 진입 query 컨텍스트(안정 참조). */
  suggestContext: ReportComposerPreviewContext;
  /** 라우트 템플릿 키(신규). 편집은 문서의 `templateKey`(non-null — 모든 문서가 양식 키를 갖는다). */
  routeTemplateKey: string;
}): MethodReresolveRequest | null {
  const {
    isEditMode,
    editReportId,
    loadedEditReport,
    companyId,
    siteIdParam,
    inspectionIds,
    suggestContext,
    routeTemplateKey,
  } = args;
  const editReport =
    isEditMode && loadedEditReport && loadedEditReport.id === editReportId ? loadedEditReport : null;
  if (isEditMode && !editReport) return null;
  const editInspectionIds = editReport ? resolveEditReportInspectionIds(editReport) : undefined;
  const contextInspectionIds = editReport ? editInspectionIds : inspectionIds;
  if (
    !companyId &&
    !(editReport ? editReport.siteId : siteIdParam) &&
    !(contextInspectionIds && contextInspectionIds.length > 0)
  ) {
    return null;
  }
  if (!editReport) {
    return { context: suggestContext, templateKey: routeTemplateKey, mergeOptions: undefined };
  }
  return {
    context: editReportPrefillContext(editReport, companyId, { inspectionIds: editInspectionIds }),
    templateKey: editReport.templateKey,
    mergeOptions: { rowBasis: 'current' },
  };
}
